/**
 * Image Upload Utility
 * Handles image validation, compression, and upload with consistent error handling
 */

import heic2any from 'heic2any';
import { ApiClient } from '@chefaiq/shared/lib/api-client';

// Use environment variable for API URL
const API_URL = import.meta.env.VITE_API_URL || "https://chefaiq.com";

interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Convert HEIC to JPEG
 */
async function convertHEICtoJPEG(file: File): Promise<File> {
  try {
    console.log('🔄 Converting HEIC to JPEG...');
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });
    
    // heic2any can return Blob or Blob[], handle both
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const convertedFile = new File([blob], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
    
    console.log('✅ HEIC converted:', {
      original: `${(file.size / 1024).toFixed(0)}KB`,
      converted: `${(convertedFile.size / 1024).toFixed(0)}KB`
    });
    
    return convertedFile;
  } catch (error) {
    console.error('❌ HEIC conversion failed:', error);
    throw new Error('Failed to convert HEIC image. Please try converting to JPG manually.');
  }
}

/**
 * Compress and convert image to JPEG
 */
async function compressImage(file: File, maxWidth: number = 1200, quality: number = 0.85): Promise<File> {
  // Skip compression for already small JPEG/PNG files
  if ((file.type === 'image/jpeg' || file.type === 'image/png') && file.size < 500 * 1024) {
    console.log('🗜️ File already small, skipping compression');
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if needed
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              // Create new File from blob
              const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg').replace(/\.(png|gif|webp)$/i, '.jpg');
              const compressedFile = new File([blob], fileName, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              
              console.log('🗜️ Compression:', {
                original: `${(file.size / 1024).toFixed(0)}KB`,
                compressed: `${(compressedFile.size / 1024).toFixed(0)}KB`,
                reduction: `${(((file.size - compressedFile.size) / file.size) * 100).toFixed(0)}%`
              });
              
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Validates image file type and size
 */
export function validateImageFile(file: File, maxSizeMB: number = 2): ValidationResult {
  // HEIC files are now supported via conversion
  const isHEIC = file.name.match(/\.(heic|heif)$/i) || file.type.match(/heic|heif/i);
  
  // Check file type (now including HEIC)
  if (!isHEIC && !file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
    return {
      valid: false,
      error: "Invalid file type. Please upload a JPG, PNG, GIF, WebP, or HEIC image."
    };
  }

  // Check file size (before compression)
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File too large. Image must be less than ${maxSizeMB}MB. Please compress or resize your image.`
    };
  }

  return { valid: true };
}

/**
 * Uploads an image file to the specified endpoint
 */
export async function uploadImage(
  file: File,
  endpoint: string,
  fieldName: string = "coverImage"
): Promise<UploadResult> {
  try {
    // Convert HEIC to JPEG first if needed
    let processedFile = file;
    const isHEIC = file.name.match(/\.(heic|heif)$/i) || file.type.match(/heic|heif/i);
    
    if (isHEIC) {
      processedFile = await convertHEICtoJPEG(file);
    }
    
    // Compress image before upload
    console.log('🗜️ Compressing image before upload...');
    const compressedFile = await compressImage(processedFile);
    
    const formData = new FormData();
    formData.append(fieldName, compressedFile);

    // Add cache-busting parameter for CloudFront
    const cacheBuster = `?t=${Date.now()}`;
    const uploadUrl = `${API_URL}${endpoint}${cacheBuster}`;

    const token = ApiClient.getToken();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers,
      body: formData
    });

    console.log('🖼️ Image upload - response status:', response.status);
    console.log('🖼️ Image upload - response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('🖼️ Image upload - error response:', errorText);
      throw new Error("Upload failed");
    }

    const { url } = await response.json();
    return { success: true, url };
  } catch (error) {
    console.error('🖼️ Image upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to upload image. Please try again."
    };
  }
}

/**
 * Validates and uploads an image in one call
 */
export async function validateAndUploadImage(
  file: File,
  endpoint: string,
  fieldName: string = "coverImage",
  maxSizeMB: number = 2
): Promise<UploadResult> {
  const validation = validateImageFile(file, maxSizeMB);
  
  if (!validation.valid) {
    return {
      success: false,
      error: validation.error
    };
  }

  return uploadImage(file, endpoint, fieldName);
}

/**
 * Convert any image (including HEIC) to compressed JPEG base64 for AI chat
 */
export async function imageFileToBase64(file: File): Promise<{ data: string; name: string; mimeType: string }> {
  // Convert HEIC to JPEG blob first using heic2any
  let sourceFile: File | Blob = file;
  if (/\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif') {
    const heic2any = (await import('heic2any')).default;
    sourceFile = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 }) as Blob;
  }
  const bitmap = await createImageBitmap(sourceFile);
  const canvas = document.createElement('canvas');
  // Start with 1200px max
  let maxSize = 1200;
  let quality = 0.75;
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  let data = canvas.toDataURL('image/jpeg', quality).split(',')[1];
  // Keep under 2MB base64 — images have a higher Claude limit than documents
  const MAX_B64 = 2 * 1024 * 1024;
  if (data.length > MAX_B64) data = canvas.toDataURL('image/jpeg', 0.6).split(',')[1];
  if (data.length > MAX_B64) {
    // Resize smaller only as last resort
    const s2 = Math.min(1, 800 / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.round(bitmap.width * s2);
    canvas.height = Math.round(bitmap.height * s2);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    data = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
  }
  const name = file.name.replace(/\.[^.]+$/, '.jpg');
  return { data, name, mimeType: 'image/jpeg' };
}
